import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { FirstVisitHint, HelpButton } from '../help/HelpPanel';
import { api } from '../lib/api';
import { useAppUpdate } from '../lib/appUpdate';
import { useStaff, type Role } from '../lib/auth';
import { roleLabel } from '../lib/format';
import { menuFor, navActive, type Menu } from '../lib/menu';
import { filterMenu, recordJumps } from '../lib/menuSearch';
import { ThemeToggle } from '../pages/staff/engagement/ThemeToggle';
import { currentTheme, toggleTheme } from '../pages/staff/engagement/theme';
import { LiveDot } from '../pages/staff/live/LiveDot';
import { useOutletList } from '../pages/staff/admin/common';
import { NavSearch } from './NavSearch';
import { NotificationBell } from './NotificationBell';
import { FIND_FROM, SearchableSelect } from './SearchableSelect';
import { ClockInPrompt, ShiftButton } from './ShiftControl';
import { Select, Spinner } from './ui';

// The shop admin's rent banner (from the collection day until paid), loaded with the first shop-admin page.
const RentDueBanner = lazy(() => import('../pages/staff/shop/RentCollection').then((m) => ({ default: m.RentDueBanner })));

// The menu itself (which pages, for whom, in what order) lives in lib/menu.ts, where unit tests can read it.
export { firstPage, menuFor, NAV, NOT_YET_LIVE, SHOP_NAV, type Menu, type NavItem } from '../lib/menu';

/** Pages that belong to one shop: opening one as the super admin opens that shop's menu. */
const SHOP_ROUTES = ['/admin/shop-home', '/admin/pos', '/admin/bills', '/admin/products', '/admin/shop-pnl', '/admin/shop-staff', '/admin/shop-settings'];
/** Pages that work for a shop or for the venue: they keep whichever menu is open. */
const SHARED_ROUTES = ['/admin/closing', '/admin/finance', '/admin/printer', '/admin/account', '/admin/appointments'];
/** Venue pages that work on one vehicle gate (the gate picker in the header chooses it). */
const GATE_ROUTES = ['/admin/gate', '/admin/collect', '/admin/visitors', '/admin/live', '/admin/closing', '/admin/printer'];

const SHOP_ROLES: Role[] = ['shop_admin', 'shop_staff'];
const under = (path: string, routes: string[]) => routes.some((r) => path === r || path.startsWith(`${r}/`));

/** Whether the key press happened while typing somewhere (a field, a text area, an editable block). */
const typingIn = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
};

/** The shop's logo for its own logins' menu (admins read it from the shop profile, counters from the billing set-up). */
function useShopLogo(outletId: string | null, enabled: boolean) {
  const { can } = useStaff();
  const viaProfile = can('shop.settings');
  return useQuery({
    queryKey: ['shell', 'shop-logo', outletId, viaProfile],
    queryFn: async () => {
      if (viaProfile) return (await api<{ logo?: { url: string | null } | null }>(`/shops/${outletId}/profile`)).logo?.url ?? null;
      return (await api<{ logoUrl?: string | null }>('/sales/config', { query: { outletId } })).logoUrl ?? null;
    },
    enabled: enabled && !!outletId && (viaProfile || can('pos.sell')),
    // Logo links are signed for a few minutes.
    staleTime: 4 * 60_000,
    retry: false,
  });
}

/**
 * Whether a shop login's own shop is run by the venue itself (it then has no venue statements). The answer
 * comes from the same request the statements page makes, so opening that page afterwards costs nothing.
 */
function useOwnShopVenueRun(outletId: string | null, enabled: boolean) {
  const q = useQuery({
    queryKey: ['settlements', 'overview', outletId],
    queryFn: () => api<{ shop?: { operator_type?: string } }>('/settlements/overview', { query: { outletId } }),
    enabled: enabled && !!outletId,
    retry: false,
    staleTime: 10 * 60_000,
  });
  return q.data?.shop?.operator_type === 'venue_operated';
}

/**
 * The menu this login sees right now (also listed, with one line each, on "What I can do"), and whether the
 * shop the super admin has open is run by the venue (it may then work at its counter).
 */
export function useStaffMenu(): Menu & { venueRun: boolean } {
  const { me, can, outlet, outletId, venueWide, viewingShop, workAtCounter, mustChangePassword } = useStaff();
  const shopLogin = SHOP_ROLES.includes(me.user.role);
  const outlets = useOutletList(venueWide && viewingShop);
  const venueRun = !!viewingShop && (outlets.data as { id: string; operator_type?: string }[] | undefined)?.find((o) => o.id === outletId)?.operator_type === 'venue_operated';
  const venueRunShop = useOwnShopVenueRun(outletId, shopLogin && can('settlements.read') && outlet?.kind === 'shop' && !mustChangePassword);
  return useMemo(
    () => ({
      ...(mustChangePassword
        ? { sections: [] }
        : menuFor(me, can, {
            viewingShop,
            workAtCounter: workAtCounter && venueRun,
            venueRunShop,
            // The shop in front of the login (its own, or the one head office has open) keeps an appointment book.
            takesAppointments: shopLogin || viewingShop ? outlet?.takes_appointments === true : undefined,
            // …and keeps an inventory of raw materials (Shops & outlets → "Inventory of raw materials").
            tracksInventory: shopLogin || viewingShop ? outlet?.tracks_inventory === true : undefined,
          })),
      venueRun,
    }),
    [me, can, viewingShop, workAtCounter, venueRun, venueRunShop, mustChangePassword, shopLogin, outlet],
  );
}

export function StaffLayout() {
  const { me, can, outlet, outletId, setOutletId, logout, venueWide, viewingShop, openShop, exitShop, workAtCounter, setWorkAtCounter, mustChangePassword } = useStaff();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // A newer release is out: this screen reloads itself at a quiet moment; the bar lets them do it now.
  const update = useAppUpdate();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const role = me.user.role;
  const shopLogin = SHOP_ROLES.includes(role);
  const menu = useStaffMenu();
  const venueRun = menu.venueRun;
  // Not before a handed-over password is changed: the API refuses everything but My account until then.
  const logo = useShopLogo(outletId, shopLogin && !mustChangePassword);
  const [logoBroken, setLogoBroken] = useState<string | null>(null);
  const gates = useMemo(() => me.outlets.filter((o) => o.kind === 'parking'), [me.outlets]);

  /*
   * The search box under the brand (every login, desktop and phone): the text narrows the menu to the pages it
   * matches (lib/menuSearch.ts), and when it looks like a record (a number plate, a mobile, a bill number, a name)
   * a "Find" group offers the list pages that can look it up. The pages come first; for a name whose words are a
   * page's name (typing "bills"), the Find rows stay out of the way.
   */
  const [query, setQuery] = useState('');
  const shown = useMemo(() => filterMenu(menu, query), [menu, query]);
  const jumps = useMemo(() => recordJumps(query, menu).filter((j) => j.kind !== 'name' || !shown.labelHit), [query, menu, shown.labelHit]);
  const searching = query.trim() !== '';
  const desktopSearch = useRef<HTMLInputElement>(null);
  // "/" on a phone-sized window opens the drawer with its box focused.
  const [focusDrawerSearch, setFocusDrawerSearch] = useState(false);

  /*
   * The drawer and the account menu close when another page opens (a tap on a link, the back button, a
   * redirect). The router swaps pages inside a transition: the address bar changes at once, but the shown
   * page (and `path`) only once the next page's code has loaded, up to a second or two later on a phone. A
   * menu opened in between used to close itself when that late page change landed. Each menu remembers the
   * address it was opened on, and only a page other than that one closes it.
   */
  const openedAt = useRef<{ drawer: string | null; account: string | null }>({ drawer: null, account: null });
  const openDrawer = () => {
    openedAt.current.drawer = window.location.pathname;
    setOpen(true);
  };
  const toggleAccount = () => {
    openedAt.current.account = window.location.pathname;
    setMenuOpen((m) => !m);
  };
  useEffect(() => {
    if (openedAt.current.drawer !== path) setOpen(false);
    if (openedAt.current.account !== path) setMenuOpen(false);
    // A new page: the menu shows everything again.
    setQuery('');
  }, [path]);
  // While the drawer is open the page behind it must not scroll, and Escape closes it.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
      setFocusDrawerSearch(false);
    };
  }, [open]);
  // "/" focuses the search box, unless the person is typing somewhere else, or on the billing screen, where "/"
  // already jumps to its product search (billing speed comes first).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey || typingIn(e.target) || e.defaultPrevented) return;
      if (/\/pos\/?$/.test(window.location.pathname)) return;
      e.preventDefault();
      const box = desktopSearch.current;
      if (box && box.offsetParent !== null) {
        box.focus();
        box.select();
      } else {
        setFocusDrawerSearch(true);
        openedAt.current.drawer = window.location.pathname;
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Venue-wide logins: a shop's page opens that shop's menu; a venue page closes it; gate pages work on a gate.
  useEffect(() => {
    if (!venueWide) return;
    if (under(path, SHOP_ROUTES)) {
      if (!viewingShop && outlet?.kind === 'shop') openShop(outlet.id);
      return;
    }
    if (viewingShop && !under(path, SHARED_ROUTES)) exitShop();
    else if (!viewingShop && under(path, GATE_ROUTES) && outlet?.kind !== 'parking' && gates[0]) setOutletId(gates[0].id);
  }, [path, venueWide, viewingShop, outlet, gates, openShop, exitShop, setOutletId]);

  // The browser tab shows the shop's name for its own logins, the venue's name for everyone else.
  const brand = shopLogin && outlet ? outlet.name : me.business.name;
  // Who is told anything (notifications/staff.ts): head office, and shop admins about their own shop.
  const bell = !mustChangePassword && (role === 'owner' || role === 'accountant' || role === 'shop_admin');
  useEffect(() => {
    document.title = brand;
  }, [brand]);

  if (mustChangePassword && path !== '/admin/account') return <Navigate to="/admin/account" replace />;

  const signOut = async () => {
    setMenuOpen(false);
    setOpen(false);
    await logout();
    navigate('/admin/login');
  };

  /** Opens a page from the search (Enter, or a tap on a Find row): the box empties and the drawer closes. */
  const go = (to: string) => {
    setQuery('');
    setOpen(false);
    navigate(to);
  };
  const onSearchEnter = () => {
    const to = shown.first?.to ?? jumps[0]?.to;
    if (to) go(to);
  };

  const logoUrl = shopLogin && logo.data && logoBroken !== logo.data ? logo.data : null;
  const brandBlock = (
    <span className="flex min-w-0 items-center gap-2.5">
      {logoUrl && <img src={logoUrl} alt="" className="h-8 w-8 shrink-0 rounded-md bg-white object-contain" onError={() => setLogoBroken(logo.data ?? null)} />}
      <span className="truncate" data-testid="shell-brand">
        {brand}
      </span>
    </span>
  );

  const sectionTitle = 'truncate px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400';
  const nav = (
    <nav aria-label="Main" className="flex flex-col gap-4 px-3 py-4">
      {viewingShop && !searching && (
        <button
          type="button"
          onClick={() => {
            exitShop();
            navigate('/admin/shops');
          }}
          className="flex min-h-11 items-center rounded-md px-3 text-left text-sm text-slate-300 hover:bg-white/10 hover:text-white lg:min-h-9"
        >
          ← All shops
        </button>
      )}
      {shown.sections.map((s) => (
        <div key={s.title}>
          <p className={sectionTitle}>{viewingShop ? (outlet?.name ?? s.title) : s.title}</p>
          {s.items.map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              end={i.to === '/admin/shops'}
              onClick={() => {
                setQuery('');
                setOpen(false);
              }}
              className={({ isActive }) =>
                clsx('block rounded-md px-3 py-3 text-sm lg:py-1.5', navActive(i, s.items, location, isActive) ? 'bg-brand-500 font-medium text-white' : 'text-slate-300 hover:bg-white/10 hover:text-white')
              }
            >
              {i.label}
            </NavLink>
          ))}
        </div>
      ))}
      {searching && shown.sections.length === 0 && (
        <p className="px-3 text-sm text-slate-400" role="status" data-testid="nav-search-empty">
          No page called “{query.trim()}”. Try another word, e.g. bills, gate or staff.
        </p>
      )}
      {jumps.length > 0 && (
        <div data-testid="nav-search-find">
          <p className={sectionTitle}>Find</p>
          {jumps.map((j) => (
            <NavLink
              key={j.to}
              to={j.to}
              onClick={() => {
                setQuery('');
                setOpen(false);
              }}
              className="block rounded-md px-3 py-3 text-sm text-slate-300 hover:bg-white/10 hover:text-white lg:py-1.5"
            >
              {j.label}
            </NavLink>
          ))}
        </div>
      )}
      {mustChangePassword && (
        <NavLink to="/admin/account" className="block rounded-md bg-brand-500 px-3 py-3 text-sm font-medium text-white lg:py-1.5">
          My account
        </NavLink>
      )}
      <div className="border-t border-white/10 pt-3">
        {/* A button like "← All shops" above: the menu's links are its pages; these two are about the login. */}
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            navigate('/admin/guide');
          }}
          aria-current={path === '/admin/guide' ? 'page' : undefined}
          className={clsx(
            'flex min-h-11 w-full items-center rounded-md px-3 text-left text-sm lg:min-h-9',
            path === '/admin/guide' ? 'bg-brand-500 font-medium text-white' : 'text-slate-300 hover:bg-white/10 hover:text-white',
          )}
        >
          What I can do
        </button>
        <button type="button" onClick={signOut} className="flex min-h-11 w-full items-center rounded-md px-3 text-left text-sm text-slate-300 hover:bg-white/10 hover:text-white lg:min-h-9">
          Sign out
        </button>
      </div>
    </nav>
  );

  // Gate staff and shop logins switch between their outlets when they have more than one; venue-wide logins have
  // no switcher (they open a shop from "All shops" and pick a gate only on gate pages, when there is more than one
  // gate). A login with a single outlet has nothing to choose, so it sees the outlet's name as plain text instead
  // (and venue staff such as housekeeping, who only sign in through the gate, see nothing).
  const picker = venueWide
    ? !viewingShop && under(path, GATE_ROUTES) && gates.length > 1
      ? { label: 'Gate', options: gates }
      : null
    : me.outlets.length > 1
      ? { label: 'Outlet', options: me.outlets }
      : null;
  const outletName = !venueWide && !picker && role !== 'venue_staff' && outlet ? outlet.name : null;
  // A phone's top bar has room for the bell or the outlet switcher, not the theme switch as well: it moves to the account menu.
  const themeInMenu = bell || !!picker;

  return (
    <div className="min-h-screen lg:flex">
      <aside className="hidden w-60 shrink-0 bg-shell lg:block">
        <div className="sticky top-0 flex max-h-screen flex-col overflow-y-auto">
          <div className="px-6 pb-2 pt-5 text-lg font-bold tracking-tight text-white">{brandBlock}</div>
          {!mustChangePassword && <NavSearch ref={desktopSearch} value={query} onChange={setQuery} onEnter={onSearchEnter} placeholder="Search pages… (press /)" className="pt-1" />}
          {nav}
        </div>
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button className="absolute inset-0 bg-slate-900/50" aria-label="Close menu" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-[min(18rem,85vw)] flex-col bg-shell shadow-2xl" aria-label="Menu">
            <div className="flex h-14 shrink-0 items-center justify-between border-b border-white/10 pl-6 pr-2 pt-[env(safe-area-inset-top)]">
              <span className="min-w-0 text-lg font-bold tracking-tight text-white">{brandBlock}</span>
              <button className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-xl text-slate-300 hover:bg-white/10" aria-label="Close menu" onClick={() => setOpen(false)}>
                ✕
              </button>
            </div>
            {!mustChangePassword && <NavSearch value={query} onChange={setQuery} onEnter={onSearchEnter} placeholder="Search pages…" autoFocus={focusDrawerSearch} className="shrink-0 pt-3" />}
            <div className="min-h-0 flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">{nav}</div>
          </aside>
        </div>
      )}
      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="flex h-14 items-center gap-2 pl-2 pr-3 sm:gap-3 sm:px-4">
            <button className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-xl text-slate-600 hover:bg-slate-100 lg:hidden" aria-label="Open menu" onClick={openDrawer}>
              ☰
            </button>
            <span className="hidden min-w-0 truncate font-semibold sm:block lg:hidden">{brand}</span>
            <div className="ml-auto flex min-w-0 flex-1 items-center justify-end gap-1 sm:flex-none sm:gap-2">
              {picker &&
                // Many gates or shops: a find button beside the switcher narrows it by name or code (also on a phone).
                (picker.options.length > FIND_FROM ? (
                  <SearchableSelect
                    compact
                    label={picker.label}
                    value={outletId ?? ''}
                    onChange={setOutletId}
                    options={picker.options}
                    className="h-11 sm:h-9 sm:w-52 sm:flex-none"
                    wrapperClassName="flex-1 sm:flex-none"
                  />
                ) : (
                  <Select
                    aria-label={picker.label}
                    value={outletId ?? ''}
                    onChange={(e) => setOutletId(e.target.value)}
                    className="h-11 min-w-0 flex-1 sm:h-9 sm:w-52 sm:flex-none"
                  >
                    {picker.options.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </Select>
                ))}
              {outletName && (
                <span className="hidden min-w-0 truncate text-sm text-slate-600 sm:block" data-testid="outlet-name">
                  {outletName}
                </span>
              )}
              {!mustChangePassword && <ShiftButton />}
              {can('live.view') && !mustChangePassword && <LiveDot />}
              {bell && <NotificationBell />}
              {/* On a phone the bell takes the theme switch's place in the top bar; the account menu has it there. */}
              <ThemeToggle className={clsx('h-11 w-11 sm:h-9 sm:w-9', themeInMenu && 'hidden sm:inline-flex')} />
              <HelpButton path={path} role={role} />
              <div className="relative shrink-0">
                <button
                  onClick={toggleAccount}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  className="flex h-11 items-center gap-2 rounded-lg px-1.5 text-left hover:bg-slate-100"
                >
                  <span aria-hidden className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-800">
                    {me.user.name.slice(0, 1).toUpperCase()}
                  </span>
                  <span className="hidden sm:block">
                    <span className="block text-sm font-medium leading-tight">{me.user.name}</span>
                    <span className="block text-xs text-slate-500">{roleLabel(role)}</span>
                  </span>
                  <span className="sr-only">Account menu</span>
                </button>
                {menuOpen && (
                  <>
                    <button className="fixed inset-0 z-40 cursor-default" aria-label="Close menu" onClick={() => setMenuOpen(false)} />
                    {/* The top bar's blur keeps the backdrop above inside the bar; this one covers the page below it. */}
                    {createPortal(<button type="button" tabIndex={-1} aria-hidden className="fixed inset-0 z-20 cursor-default" onClick={() => setMenuOpen(false)} />, document.body)}
                    <div role="menu" className="absolute right-0 z-50 mt-1 w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                      <p className="border-b border-slate-100 px-3 py-2 text-xs text-slate-500 sm:hidden">
                        {me.user.name} · {roleLabel(role)}
                      </p>
                      <button
                        role="menuitem"
                        onClick={() => {
                          setMenuOpen(false);
                          navigate('/admin/account');
                        }}
                        className="block min-h-11 w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                      >
                        My account
                      </button>
                      <button
                        role="menuitem"
                        onClick={() => {
                          setMenuOpen(false);
                          navigate('/admin/guide');
                        }}
                        className="block min-h-11 w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                      >
                        What I can do
                      </button>
                      {themeInMenu && (
                        <button
                          role="menuitem"
                          onClick={() => {
                            setMenuOpen(false);
                            toggleTheme();
                          }}
                          className="block min-h-11 w-full px-3 py-2 text-left text-sm hover:bg-slate-50 sm:hidden"
                        >
                          {currentTheme() === 'dark' ? 'Light theme' : 'Dark theme'}
                        </button>
                      )}
                      <button role="menuitem" onClick={signOut} className="block min-h-11 w-full px-3 py-2 text-left text-sm text-red-700 hover:bg-slate-50">
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          {viewingShop && outlet && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-brand-100 bg-brand-50 px-4 py-1.5 text-sm text-brand-900" role="region" aria-label="Shop you are viewing">
              <p className="min-w-0 flex-1 basis-full truncate sm:basis-auto">
                Viewing <strong className="font-semibold">{outlet.name}</strong>
              </p>
              {venueRun && can('pos.sell') && (
                <label className="flex min-h-11 cursor-pointer items-center gap-2 lg:min-h-9">
                  <input type="checkbox" className="h-4 w-4 accent-brand-700" checked={workAtCounter} onChange={(e) => setWorkAtCounter(e.target.checked)} />
                  Work at this counter
                </label>
              )}
              <button
                type="button"
                onClick={() => {
                  exitShop();
                  navigate('/admin/shops');
                }}
                className="min-h-11 rounded-md px-3 font-medium text-brand-800 ring-1 ring-brand-200 hover:bg-white lg:min-h-8"
              >
                Exit
              </button>
            </div>
          )}
        </header>
        <main className="mx-auto max-w-7xl px-4 py-5 sm:py-6">
          {update.ready && (
            <div role="status" className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-sky-50 px-3 py-2 text-sm text-sky-900 ring-1 ring-sky-200">
              <span>A new version of the app is ready. This screen refreshes by itself when it is not in use.</span>
              <button type="button" onClick={update.reload} className="min-h-11 rounded-md bg-sky-700 px-3 font-medium text-white hover:bg-sky-800 lg:min-h-8">
                Refresh now
              </button>
            </div>
          )}
          {me.outlets.length === 0 && !['owner', 'accountant', 'venue_staff'].includes(role) && path !== '/admin/account' ? (
            <p className="rounded-lg bg-amber-50 p-4 text-sm text-amber-900">You are not assigned to an outlet or shop yet. Ask the super admin (or your shop admin) to assign you.</p>
          ) : (
            // Pages load on demand: only the page area waits, so the menu and header stay put (and an open
            // phone menu stays open) while the next page's code arrives.
            <Suspense fallback={<Spinner />}>
              {!mustChangePassword && <ClockInPrompt />}
              {!mustChangePassword && <FirstVisitHint path={path} role={role} userId={me.user.id} />}
              {!mustChangePassword && role === 'shop_admin' && outletId && outlet?.kind === 'shop' && path !== '/admin/rent' && path !== '/admin/shop-home' && <RentDueBanner outletId={outletId} compact />}
              <Outlet />
            </Suspense>
          )}
        </main>
      </div>
    </div>
  );
}
